import {
  OrderResponse,
  isFailed,
} from '@payments-checkout/shared/helpers/constants/order-status.const';
import { StringManager } from '@payments-checkout/shared/helpers/index';
import { instance } from '@payments-checkout/shared/helpers/service/instance.service';
import { setObservabilityContext } from '@payments-checkout/shared/observability';
import {
  usePaymentMethod,
  usePaymentResponse,
  useSessionId,
} from '@payments-checkout/shared/providers';
import { UseMutationOptions, useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { env } from 'next-runtime-env';
import { useParams, useRouter } from 'next/navigation';
import { errorMessagePaymentGeneric } from '../../components/pages/cardAddress/errorMessage';
import { usePageSharedData } from '../../hooks/usePageSharedData';
import { analiticaUsePaymentEvents } from './analitica';
import { PaymentPayload } from './buildPaymentPayload';

type PaymentDataProps = PaymentPayload;

export async function sendPayment(
  paymentLinkId: string,
  paymentData: PaymentDataProps,
  sessionId?: string
) {
  if (!sessionId) throw new Error('Session ID is required');

  const recaptchaToken = await getRecaptchaToken();

  const response = await instance.patch<OrderResponse>(
    getPaymentLinkUrl(paymentLinkId, sessionId),
    { ...paymentData },
    {
      headers: {
        'g-recaptcha-response': recaptchaToken,
      },
    }
  );

  return response.data;
}

function getPaymentLinkUrl(paymentLinkId: string, sessionId: string) {
  const basPaymentLinkUrl = `paymentlinks/${paymentLinkId}/sessions/${sessionId}/payment`;
  if (env('NEXT_PUBLIC_ENV') === 'production') {
    return `with-recaptcha/${basPaymentLinkUrl}`;
  }
  return basPaymentLinkUrl;
}

export const usePayment = (
  options?: Omit<
    UseMutationOptions<unknown, AxiosError, { paymentData: PaymentDataProps }>,
    'mutationFn' | 'onSuccess'
  >
) => {
  const params = useParams<{ paymentLinkId: string }>();
  const { push } = useRouter();
  const { sessionId } = useSessionId();
  const { setPaymentResponse } = usePaymentResponse();
  const { paymentMethod, setFailedPaymentMessage, setShowFailedPaymentAlert} = usePaymentMethod();
  const { isRecurrence } = usePageSharedData();

  return useMutation({
    ...options,
    mutationFn: ({
      paymentData,
    }: {
      paymentData: PaymentDataProps;
      recaptchaToken?: string;
    }) => sendPayment(params.paymentLinkId, paymentData, sessionId),
    onSuccess: (data) => {
      setObservabilityContext({
        order_id: (data as OrderResponse)?.order_id,
        payment_method: paymentMethod ?? undefined,
      });
      setPaymentResponse(data);
      if( isFailed(data.order_status)) {
        setShowFailedPaymentAlert(true);
        setFailedPaymentMessage(errorMessagePaymentGeneric(1000));
      } else {
        if (isRecurrence) {
          push(
            `/${
              params.paymentLinkId
            }/${StringManager.convertFromSnakeCaseToCamelCase(
              paymentMethod ?? 'credit_card'
            )}-success`
          );
        } else {
          push(`/status/${sessionId}`);
        }
      }
    },
    retry: false,
  });
};

const RECAPTCHA_READY_TIMEOUT_MS = 10_000;

function waitForRecaptchaReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(
      () => reject(new Error(`reCAPTCHA ready timeout: script did not load within ${RECAPTCHA_READY_TIMEOUT_MS}ms`)),
      RECAPTCHA_READY_TIMEOUT_MS
    );

    grecaptcha.ready(() => {
      clearTimeout(timeoutId);
      resolve();
    });
  });
}

export async function getRecaptchaToken(): Promise<string | undefined> {
  if (env('NEXT_PUBLIC_ENV') !== 'production') {
    return '';
  }

  await waitForRecaptchaReady();
  analiticaUsePaymentEvents.events.recaptcha.onReady();

  try {
    const token = await grecaptcha.execute(
      env('NEXT_PUBLIC_RECAPTCHA_V3_TOKEN') ?? '',
      { action: 'click' }
    );

    analiticaUsePaymentEvents.events.recaptcha.onExecuteSuccess();
    return token;
  } catch (error) {
    analiticaUsePaymentEvents.events.recaptcha.onExecuteError();
    throw error;
  }
}
